/**
 * Apps: what the agents of this workspace can reach. One AI account is required — every agent runs on
 * it. A broker is optional: none, one or several. Then the ways agents reach you and read the web, and
 * any MCP server you add by its address. Market data is not here: it comes with Liberabank.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { ChevronRight, Plus, Search, Trash2 } from 'lucide-react'
import { create } from 'zustand'
import { BrokerSettings } from '../components/settings/BrokerSettings'
import { RobinhoodSettings } from '../components/settings/RobinhoodSettings'
import { BrowserDevicesSettings, TelegramSettings } from '../components/settings/SettingsPage'
import { XaiSubscriptionSettings } from '../components/settings/XaiSubscriptionSettings'
import { api, connectorsApi, robinhoodApi, xaiApi, type Connector, type ConnectorTool, type RobinhoodStatus, type XaiSubscriptionStatus } from './api'
import { Badge, Btn, Card, Dialog, Field, I, Status, Switch, Tabs, askSure, cx, inputCls, type PopEntry } from './kit'
import { Logo, type LogoName } from './logos'
import { Bar, Page, useTabs } from './Page'
import { useDemo } from './store'

type Kind = 'ai' | 'broker' | 'app' | 'connector'
interface AppCard {
  id: string
  kind: Kind
  name: string
  about: string
  logo: LogoName
  connected: boolean
  /** Comes with Liberabank: there is nothing to connect. */
  builtIn?: boolean
  required?: boolean
  /** Something is wrong and needs the user (a sign-in that ran out). */
  warn?: string
  connector?: Connector
}

const useApps = create<{ q: string; adding: boolean; openId: string | null; stamp: number }>(() => ({ q: '', adding: false, openId: null, stamp: 0 }))
const reload = () => useApps.setState((s) => ({ stamp: s.stamp + 1 }))

/** What is connected, read from the server (and read again whenever a window that changes it closes). */
function useConnections() {
  const stamp = useApps((s) => s.stamp)
  const admin = useDemo((s) => s.user?.role === 'admin')
  const settings = useDemo((s) => s.settings)
  const health = useDemo((s) => s.health)
  const [xai, setXai] = useState<XaiSubscriptionStatus | null>(null)
  const [rh, setRh] = useState<RobinhoodStatus | null>(null)
  const [devices, setDevices] = useState(0)
  const [connectors, setConnectors] = useState<Connector[]>([])
  useEffect(() => {
    let on = true
    void xaiApi.status().then((x) => on && setXai(x), () => {})
    void robinhoodApi.status().then((x) => on && setRh(x), () => {})
    void api.bridgeDevices().then((x) => on && setDevices(x.devices.length), () => {})
    void connectorsApi.list().then((x) => on && setConnectors(x), () => {})
    return () => {
      on = false
    }
  }, [stamp])
  const cards: AppCard[] = [
    {
      id: 'grok',
      kind: 'ai',
      name: 'Grok',
      logo: 'grok',
      required: true,
      connected: !!xai?.connected || (admin && !!health?.xai),
      warn: xai?.status === 'needs_login' ? 'Sign in again' : xai?.status === 'tier_denied' ? 'This plan cannot be used here' : !xai?.connected && !admin ? 'Sign in to let your agents answer' : undefined,
      about: xai?.connected ? `Every agent runs on your own Grok plan${xai.email ? ` · ${xai.email}` : ''}.` : admin && health?.xai ? 'Agents run on the server’s API key. Sign in to use your own Grok plan instead.' : 'Sign in with your Grok account: every agent runs on your own plan.',
    },
    { id: 'robinhood', kind: 'broker', name: 'Robinhood', logo: 'robinhood', connected: !!rh?.connected, warn: rh?.status === 'needs_login' ? 'Sign in again' : undefined, about: rh?.connected ? `Agents draft, you approve, orders go here${rh.account ? ` · account ··${String(rh.account).slice(-4)}` : ''}.` : 'Connect your Agentic account: agents draft, you approve, orders go there.' },
    ...(admin ? [{ id: 'ibkr', kind: 'broker' as const, name: 'Interactive Brokers', logo: 'ibkr' as const, connected: false, about: 'A second account for the same agents, through its own gateway and its own live prices.' }] : []),
    { id: 'telegram', kind: 'app', name: 'Telegram', logo: 'telegram', connected: !!settings?.telegramConfigured, about: 'Agents reach you here when something needs you, with a link back.' },
    { id: 'chrome', kind: 'app', name: 'Chrome', logo: 'chrome', connected: devices > 0, about: devices > 0 ? `${devices} ${devices === 1 ? 'browser' : 'browsers'} paired: an agent reads a page through it when the site blocks robots.` : 'Lets an agent read a page through your own browser when the site blocks robots.' },
    { id: 'x', kind: 'app', name: 'X', logo: 'x', connected: true, builtIn: true, about: 'Agents search posts for what people say about a stock, right now.' },
    { id: 'docker', kind: 'app', name: 'Code sandbox', logo: 'docker', connected: true, builtIn: true, about: 'An isolated machine for data work: files in, a program runs, a result out.' },
    ...connectors.map((c) => ({ id: c.id, kind: 'connector' as const, name: c.label, logo: 'mcp' as const, connected: c.enabled && c.status === 'active', warn: c.status === 'needs_login' ? 'Sign in again' : c.status === 'pending' ? 'Waiting for sign-in' : undefined, about: `${c.tools.length ? `${c.allowedTools.length ? c.allowedTools.length : c.tools.length} of ${c.tools.length} tools · ` : ''}${c.description || c.url.replace(/^https?:\/\//, '')}`, connector: c })),
  ]
  return { cards, connectors }
}

export function AppsBar() {
  const t = useTabs()
  const q = useApps((s) => s.q)
  return (
    <Bar
      right={
        <>
          <label className="flex h-8 w-[168px] items-center gap-2 rounded-[8px] border border-ws-hair pl-[7px] pr-2 transition-colors duration-150 focus-within:border-ws-focus max-sm:hidden">
            <I icon={Search} className="text-ws-icon" />
            <input value={q} onChange={(e) => useApps.setState({ q: e.target.value })} placeholder="Search" aria-label="Search apps" className="h-full min-w-0 flex-1 tracking-normal" />
          </label>
          <Btn icon={Plus} className="ml-1 px-2 max-md:hidden" onClick={() => useApps.setState({ adding: true })}>
            Custom connector
          </Btn>
        </>
      }
    >
      <Tabs
        options={[
          { id: 'all', label: 'All' },
          { id: 'connected', label: 'Connected' },
          { id: 'off', label: 'Not connected' },
        ]}
        value={t.apps}
        onChange={(v) => t.set({ apps: v })}
      />
    </Bar>
  )
}

const SECTIONS: { kind: Kind; title: string; note: string }[] = [
  { kind: 'ai', title: 'AI account', note: 'Required — every agent runs on it' },
  { kind: 'broker', title: 'Brokers', note: 'Optional — none, one or several' },
  { kind: 'app', title: 'Apps', note: '' },
  { kind: 'connector', title: 'Custom connectors', note: 'MCP servers you added' },
]

function menuOf(a: AppCard): PopEntry[] {
  if (!a.connector) return []
  const c = a.connector
  return [
    { label: c.enabled ? 'Turn off' : 'Turn on', onSelect: () => void connectorsApi.patch(c.id, { enabled: !c.enabled }).then(reload) },
    'rule',
    { label: 'Remove', icon: <I icon={Trash2} />, danger: true, onSelect: () => removeConnector(c) },
  ]
}
const removeConnector = (c: Connector) =>
  askSure({
    title: `Remove ${c.label}?`,
    body: 'Agents lose its tools at once, and the permission it gave is deleted from this server. Nothing on the server itself is touched.',
    cta: 'Remove',
    done: () => {
      useApps.setState({ openId: null })
      void connectorsApi.remove(c.id).then(() => (reload(), useDemo.getState().say(`${c.label} removed`)))
    },
  })

export function AppsView() {
  const t = useTabs()
  const { cards } = useConnections()
  const { q: raw, adding, openId } = useApps()
  const q = raw.trim().toLowerCase()
  const shown = (a: AppCard) => (t.apps === 'connected' ? a.connected : t.apps === 'off' ? !a.connected : true) && (!q || `${a.name} ${a.about}`.toLowerCase().includes(q))
  const open = cards.find((a) => a.id === openId)
  // Back from a server's own sign-in page: its connector opens by itself.
  useEffect(() => {
    const m = location.search.match(/[?&]connector=([\w-]+)/)
    const bad = location.search.match(/[?&]connector_error=([^&]+)/)
    if (!m && !bad) return
    history.replaceState(null, '', `${location.pathname}${location.hash}`)
    if (bad) return useDemo.getState().say(decodeURIComponent(bad[1]))
    useApps.setState({ openId: m![1] })
    reload()
  }, [])
  return (
    <Page>
      <div className="space-y-7">
        {SECTIONS.map((sec) => {
          const list = cards.filter((a) => a.kind === sec.kind && shown(a))
          const add = sec.kind === 'connector' && t.apps !== 'off' && !q
          if (!list.length && !add) return null
          return (
            <section key={sec.kind}>
              <div className="flex h-8 items-center gap-3 pl-2 pr-1">
                <h2 className="m-0 text-[14px] font-normal leading-5 tracking-normal text-ws-fg">{sec.title}</h2>
                {sec.note && <span className="truncate text-[12px] tracking-normal text-ws-fg-4">{sec.note}</span>}
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {list.map((a) => (
                  <Card key={a.id} as="button" onClick={() => useApps.setState({ openId: a.id })} menu={a.connector ? () => menuOf(a) : undefined} className="h-[94px] pl-3 pr-2 pt-[10px]">
                    <span className="flex h-7 items-center">
                      <Logo name={a.logo} />
                      <span className="ml-3 min-w-0 truncate font-medium text-ws-fg">{a.name}</span>
                      <span className="ml-auto shrink-0 pl-2 pr-1">{a.warn ? <Status tone="warn">{a.warn}</Status> : a.required && a.connected ? <Badge>Required</Badge> : a.builtIn ? <Status tone="off">Built in</Status> : a.connected ? <Status tone="free">{a.kind === 'connector' ? 'On' : 'Connected'}</Status> : a.kind === 'connector' ? <Status tone="off">Off</Status> : null}</span>
                    </span>
                    <span className={cx('mt-[10px] line-clamp-2 text-[12px] leading-[15px] tracking-normal', a.connected ? 'text-ws-fg-3' : 'text-ws-fg-4')}>{a.about}</span>
                  </Card>
                ))}
                {add && (
                  <button type="button" onClick={() => useApps.setState({ adding: true })} className="flex h-[94px] flex-col items-start rounded-[10px] border border-dashed border-ws-edge pl-3 pr-2 pt-[10px] text-left transition-colors duration-150 hover:border-ws-rule hover:bg-ws-hover">
                    <span className="flex h-7 items-center">
                      <span className="flex h-6 w-6 items-center justify-center rounded-[6px] border border-ws-edge text-ws-icon">
                        <I icon={Plus} />
                      </span>
                      <span className="ml-3 font-medium text-ws-fg-2">Add custom connector</span>
                    </span>
                    <span className="mt-[10px] text-[12px] leading-[15px] tracking-normal text-ws-fg-4">Any MCP server, by its address. Its tools become tools of your agents.</span>
                  </button>
                )}
              </div>
            </section>
          )
        })}
      </div>
      <p className="m-0 mt-8 flex items-center gap-3 border-t border-ws-line pl-2 pt-4 text-[12px] leading-[18px] tracking-normal text-ws-fg-4">
        <Logo name="eodhd" size={20} />
        Market data is built in: prices, fundamentals and calendars come with Liberabank. There is nothing to connect.
      </p>

      <AppDialog app={open} onClose={() => (useApps.setState({ openId: null }), reload())} />
      <AddConnector open={adding} onClose={() => (useApps.setState({ adding: false }), reload())} />
    </Page>
  )
}

const Info = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex min-h-9 items-center gap-3 px-3 py-[6px] text-[13px] tracking-normal">
    <span className="w-[112px] shrink-0 text-ws-fg-3">{label}</span>
    <span className="min-w-0 flex-1 text-ws-fg-2">{children}</span>
  </div>
)
const box = 'divide-y divide-ws-hair rounded-[8px] border border-ws-line'
const head = 'm-0 mb-[6px] mt-4 text-[12px] font-normal leading-4 tracking-normal text-ws-label'

/** The tools of a server, each one allowed or not. */
function Tools({ tools, allowed, onChange }: { tools: ConnectorTool[]; allowed: string[]; onChange: (names: string[]) => void }) {
  // No list kept = every tool is allowed.
  const on = (name: string) => !allowed.length || allowed.includes(name)
  const flip = (name: string) => {
    const now = tools.filter((t) => (t.name === name ? !on(name) : on(t.name))).map((t) => t.name)
    onChange(now.length === tools.length ? [] : now)
  }
  return (
    <div className={cx(box, 'max-h-[280px] overflow-y-auto')}>
      {tools.map((t) => (
        <div key={t.name} className="flex min-h-10 items-center gap-3 px-3 py-[6px]">
          <span className="min-w-0 flex-1">
            <span className={cx('block truncate font-mono text-[12px] leading-[18px] tracking-normal', on(t.name) ? 'text-ws-fg' : 'text-ws-fg-3')}>{t.name}</span>
            {t.description && <span className="block truncate text-[12px] leading-4 tracking-normal text-ws-fg-3">{t.description}</span>}
          </span>
          <Switch on={on(t.name)} onChange={() => flip(t.name)} label={t.name} />
        </div>
      ))}
    </div>
  )
}

/** The real settings of an app from before agents, shown inside its window. */
const Embedded = ({ children }: { children: ReactNode }) => <div className="lb-legacy lb-embed">{children}</div>

function AppDialog({ app, onClose }: { app?: AppCard; onClose: () => void }) {
  const say = useDemo((s) => s.say)
  const [c, setC] = useState<Connector | null>(null)
  useEffect(() => setC(app?.connector ?? null), [app?.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const patch = useCallback(
    (p: Parameters<typeof connectorsApi.patch>[1]) => {
      if (!c) return
      setC({ ...c, ...p } as Connector)
      void connectorsApi.patch(c.id, p).catch((e) => say(e instanceof Error ? e.message : 'That did not work'))
    },
    [c, say],
  )
  const wide = app && app.id !== 'x' && app.id !== 'docker'
  return (
    <Dialog
      open={!!app}
      onClose={onClose}
      title={app?.name ?? ''}
      width={wide ? 560 : 460}
      footer={
        app && (
          <>
            {c && (
              <Btn variant="danger" onPanel className="mr-auto" onClick={() => removeConnector(c)}>
                Remove
              </Btn>
            )}
            <Btn variant="white" onClick={onClose}>
              Done
            </Btn>
          </>
        )
      }
    >
      {app && (
        <>
          <div className="flex h-8 items-center gap-3">
            <Logo name={app.logo} size={32} />
            {app.warn ? <Status tone="warn">{app.warn}</Status> : app.builtIn ? <Status tone="off">Built in</Status> : <Status tone={app.connected ? 'free' : 'off'}>{app.connected ? (c ? 'On' : 'Connected') : c ? 'Off' : 'Not connected'}</Status>}
            {c && c.status === 'active' && (
              <span className="ml-auto">
                <Switch on={c.enabled} onChange={(v) => patch({ enabled: v })} label="Agents may use it" />
              </span>
            )}
          </div>
          <p className="m-0 mt-4 leading-[21px] tracking-normal text-ws-fg-2">{c ? c.description || 'A server you added.' : app.about}</p>

          {app.id === 'grok' && (
            <Embedded>
              <XaiSubscriptionSettings />
            </Embedded>
          )}
          {app.id === 'robinhood' && (
            <Embedded>
              <RobinhoodSettings />
            </Embedded>
          )}
          {app.id === 'ibkr' && (
            <Embedded>
              <BrokerSettings />
            </Embedded>
          )}
          {app.id === 'telegram' && (
            <Embedded>
              <TelegramSettings />
            </Embedded>
          )}
          {app.id === 'chrome' && (
            <Embedded>
              <BrowserDevicesSettings />
            </Embedded>
          )}
          {app.builtIn && <p className="m-0 mt-3 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">It comes with Liberabank: there is nothing to connect, and every agent can use it.</p>}

          {c && (
            <>
              <h3 className={head}>Server</h3>
              <div className={box}>
                <Info label="Address">
                  <span className="block truncate font-mono text-[12px]">{c.url}</span>
                </Info>
                <Info label="Access">
                  {c.auth === 'oauth' ? (
                    <span className="flex items-center justify-between gap-3">
                      {c.status === 'active' ? 'You signed in at the server' : c.status === 'pending' ? 'The sign-in was not finished' : 'The sign-in ran out'}
                      <button type="button" onClick={() => void connectorsApi.signIn(c.id).then((r) => (window.location.href = r.url), (e) => say(e instanceof Error ? e.message : 'Could not start the sign-in'))} className="shrink-0 text-[12px] text-ws-fg-3 transition-colors hover:text-ws-fg">
                        {c.status === 'active' ? 'Sign in again' : 'Sign in'}
                      </button>
                    </span>
                  ) : c.auth === 'token' ? (
                    'A token, sent with every request'
                  ) : (
                    'Open: the server asks for nothing'
                  )}
                </Info>
                {c.lastError && <Info label="Last problem">{c.lastError}</Info>}
              </div>
              {c.tools.length > 0 && (
                <>
                  <h3 className={cx(head, 'flex items-baseline justify-between')}>
                    Tools agents may use
                    <span className="flex items-center gap-3 text-ws-fg-4">
                      {c.allowedTools.length || c.tools.length} of {c.tools.length}
                      <button type="button" className="text-ws-fg-3 transition-colors hover:text-ws-fg" onClick={() => void connectorsApi.refresh(c.id).then((x) => (setC(x), say('Read again from the server')), (e) => say(e instanceof Error ? e.message : 'The server did not answer'))}>
                        Read again
                      </button>
                    </span>
                  </h3>
                  <Tools tools={c.tools} allowed={c.allowedTools} onChange={(allowedTools) => patch({ allowedTools })} />
                </>
              )}
            </>
          )}
        </>
      )}
    </Dialog>
  )
}

/** The host of an address, for saying where you are being sent. */
const hostOf = (url: string) => url.replace(/^https?:\/\//i, '').split(/[/?#]/)[0]

/**
 * Add an MCP server by its address. One window, in steps: say where it is; if the server asks you to
 * sign in, you are sent to its own page and brought back; then you see the tools it offers and choose
 * which of them agents may use.
 */
function AddConnector({ open, onClose }: { open: boolean; onClose: () => void }) {
  const say = useDemo((s) => s.say)
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [about, setAbout] = useState('')
  const [token, setToken] = useState('')
  const [more, setMore] = useState(false)
  const [step, setStep] = useState<'where' | 'reading' | 'signin' | 'tools'>('where')
  const [tools, setTools] = useState<ConnectorTool[]>([])
  const [allowed, setAllowed] = useState<string[]>([])
  const [problem, setProblem] = useState('')
  const [busy, setBusy] = useState(false)
  useEffect(() => {
    if (open) (setName(''), setUrl(''), setAbout(''), setToken(''), setMore(false), setStep('where'), setTools([]), setAllowed([]), setProblem(''), setBusy(false))
  }, [open])
  const address = url.trim()
  const host = hostOf(address)
  const plain = /^http:\/\//i.test(address)
  const ok = !!name.trim() && /^https:\/\/[^\s/]+\.[^\s]+/i.test(address)
  const read = async () => {
    setStep('reading')
    setProblem('')
    try {
      const r = await connectorsApi.probe(address, token.trim() || undefined)
      if (r.kind === 'tools') (setTools(r.tools), setAllowed([]), setStep('tools'))
      else if (r.kind === 'signin') setStep('signin')
      else (setProblem(r.message), setStep('where'))
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'The server did not answer')
      setStep('where')
    }
  }
  const finish = async () => {
    setBusy(true)
    try {
      await connectorsApi.create({ label: name.trim(), url: address, description: about.trim(), token: token.trim() || undefined, allowedTools: allowed })
      say(`${name.trim()} added — agents can use ${allowed.length || tools.length} tools`)
      onClose()
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'It could not be added')
    } finally {
      setBusy(false)
    }
  }
  const goSignIn = async () => {
    setBusy(true)
    try {
      const r = await connectorsApi.startSignIn({ label: name.trim(), url: address, description: about.trim() })
      window.location.href = r.url // the server's own page; it brings you back here
    } catch (e) {
      setProblem(e instanceof Error ? e.message : 'The sign-in could not be started')
      setBusy(false)
    }
  }
  const who = (
    <div className="flex items-center gap-3">
      <Logo name="mcp" size={32} />
      <span className="min-w-0">
        <span className="block truncate font-medium text-ws-fg">{name.trim()}</span>
        <span className="block truncate font-mono text-[12px] tracking-normal text-ws-fg-3">{address}</span>
      </span>
    </div>
  )
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add custom connector"
      width={480}
      footer={
        step === 'tools' ? (
          <>
            <Btn variant="outline" onPanel className="mr-auto" onClick={() => setStep('where')}>
              Back
            </Btn>
            <Btn variant="white" disabled={busy} onClick={() => void finish()}>
              Add connector
            </Btn>
          </>
        ) : step === 'signin' ? (
          <>
            <Btn variant="outline" onPanel className="mr-auto" onClick={() => setStep('where')}>
              Back
            </Btn>
            <Btn variant="white" disabled={busy} onClick={() => void goSignIn()}>
              {busy ? `Taking you to ${host}…` : 'Continue to sign in'}
            </Btn>
          </>
        ) : (
          <>
            <Btn variant="outline" onPanel onClick={onClose}>
              Cancel
            </Btn>
            <Btn variant="white" disabled={!ok || step === 'reading'} onClick={() => void read()}>
              {step === 'reading' ? 'Reading the server…' : 'Continue'}
            </Btn>
          </>
        )
      }
    >
      {step === 'tools' ? (
        <>
          {who}
          <h3 className={cx(head, 'flex items-baseline justify-between')}>
            The server offers {tools.length} {tools.length === 1 ? 'tool' : 'tools'}
            <span className="text-ws-fg-4">{allowed.length || tools.length} allowed</span>
          </h3>
          <Tools tools={tools} allowed={allowed} onChange={setAllowed} />
          <p className="m-0 mt-3 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">Leave off what agents do not need: fewer tools keep them quicker, and a tool that writes can change things on that server.</p>
          {problem && <p className="m-0 mt-3 text-[12px] leading-[18px] tracking-normal text-ws-down">{problem}</p>}
        </>
      ) : step === 'signin' ? (
        <>
          {who}
          <h3 className={head}>This server asks you to sign in</h3>
          <div className={box}>
            <Info label="You are sent to">
              <span className="font-mono text-[12px]">{host}</span>
            </Info>
            <Info label="There">You sign in and allow Liberabank to use its tools</Info>
            <Info label="Then">You are brought back here, and the tools are read</Info>
          </div>
          <p className="m-0 mt-3 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">Liberabank never sees your password there. It keeps only the permission the server hands back, sealed on this server; you can take it away at the server or by removing the connector.</p>
          {problem && <p className="m-0 mt-3 text-[12px] leading-[18px] tracking-normal text-ws-down">{problem}</p>}
        </>
      ) : (
        <div className="space-y-4">
          <Field label="Name">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Filings" className={inputCls} autoFocus />
          </Field>
          <Field label="MCP server address" hint={plain ? 'Use an https address' : undefined}>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/mcp" className={cx(inputCls, 'font-mono text-[12.5px]')} inputMode="url" spellCheck={false} />
          </Field>
          <div>
            <button type="button" onClick={() => setMore(!more)} aria-expanded={more} className="flex items-center gap-1 text-[12px] tracking-normal text-ws-fg-3 transition-colors hover:text-ws-fg">
              <ChevronRight size={12} strokeWidth={2} className={cx('transition-transform duration-150', more && 'rotate-90')} />
              More
            </button>
            {more && (
              <div className="mt-3 space-y-4">
                <Field label="What it is for" hint="Optional">
                  <input value={about} onChange={(e) => setAbout(e.target.value)} placeholder="Helps agents know when to use it" className={inputCls} />
                </Field>
                <Field label="Access token" hint="Only if the server gave you one">
                  <input value={token} onChange={(e) => setToken(e.target.value)} type="password" placeholder="Sent to the server with every request" className={inputCls} autoComplete="off" />
                </Field>
              </div>
            )}
          </div>
          {problem && <p className="m-0 text-[12px] leading-[18px] tracking-normal text-ws-down">{problem}</p>}
          <p className="m-0 rounded-[8px] border border-ws-line px-3 py-[10px] text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
            Add only servers you trust. Its tools receive what your agents send them, and Liberabank cannot check what a server does with it or whether its tools change later. If the server asks you to sign in, you are sent to its own page and brought back.
          </p>
        </div>
      )}
    </Dialog>
  )
}
